import {
  Component,
  ViewChild,
  ElementRef,
  AfterViewInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
} from '@angular/core';

export interface DropdownOption {
  label: string;
  value: string | number;
  subLabel?: string;
}

export interface DropdownEventDetail {
  target: {
    value: string;
  };
}

export interface DropdownChangeEvent extends CustomEvent<DropdownEventDetail> {
  detail: DropdownEventDetail;
}

interface DropdownAttributes {
  'aria-label': string;
  class: string;
  disabled?: string;
  id: string;
  label: string;
  name: string;
  options: string;
  placeholder: string;
  value: string;
  style: string;
}

@Component({
  selector: 'app-dropdown',
  templateUrl: './dropdown.html',
  styleUrls: ['./dropdown.scss'],
})
export class Dropdown implements AfterViewInit, OnChanges {
  @ViewChild('dropdownTpl', { static: true }) dropdownTpl!: ElementRef<HTMLDivElement>;

  @Input() ariaLabel = 'Select an option';
  @Input() className = '';
  @Input() disabled = false;
  @Input() height: number | string = 'auto';
  @Input() id = '';
  @Input() label = 'Options';
  @Input() name = '';
  @Input() options: DropdownOption[] = [];
  @Input() placeholder = 'Select an option';
  @Input() value = '';
  @Input() width: number | string = 'auto';

  @Output() readonly emitChange = new EventEmitter<string>();

  ngAfterViewInit() {
    if (!this.value && this.label && this.ariaLabel && this.options.length > 0) {
      Log.warn('Dropdown component is missing a value, label, ariaLabel, or options.');
      return;
    }

    const element = document.createElement('ds-dropdown');
    this.setDropdownAttributes(element);
    this.attachDropdownEvents(element);
    this.dropdownTpl.nativeElement.appendChild(element);
  }

  /**
   * Sets all relevant attributes on the dropdown element.
   * @param element The ds-dropdown element to update
   */
  private setDropdownAttributes(element: HTMLElement) {
    const attributes: DropdownAttributes = {
      'aria-label': this.ariaLabel,
      class: this.className,
      disabled: this.disabled ? 'true' : 'false',
      id: this.id,
      label: this.label,
      name: this.name,
      options: JSON.stringify(this.options),
      placeholder: this.placeholder,
      value: this.value,
      style: `width: ${this.width}; height: ${this.height};`,
    };

    Object.entries(attributes).forEach(([key, value]) => {
      if (value !== undefined) {
        element.setAttribute(key, value);
      } else {
        element.removeAttribute(key);
      }
    });
  }

  /**
   * Attaches event listeners to the dropdown element.
   * @param element The ds-dropdown element to attach events to
   */
  private attachDropdownEvents(element: HTMLElement) {
    element.addEventListener('ds-change', (event: Event) => {
      const customEvent = event as DropdownChangeEvent;
      const value = customEvent.detail?.target?.value ?? '';
      this.value = value;
      this.emitChange.emit(value);
    });
  }

  ngOnChanges() {
    if (!this.dropdownTpl?.nativeElement?.querySelector) return;
    const dropdownElement = this.dropdownTpl.nativeElement.querySelector('ds-dropdown');

    if (!dropdownElement) return;
    this.setDropdownAttributes(dropdownElement as HTMLElement);
  }
}
